import React, { useState, useMemo, useEffect, useCallback } from "react";

import _ from "lodash";
import cx from "classnames";

import { AddAssetsDialog } from "src/components/asset/add";

import { Carousel } from "react-bootstrap";
import { useDispatch } from "react-redux";
import Thumbnail from "src/components/thumbnail";
import Text from "src/components/text";
import { fetchVoicePresets } from "src/redux/actions/coaching/podcast";
import { ChatContainer } from "../chat";



import useMeasure from "react-use-measure";
import { Podcast, VoicePreset } from "src/types/coaching/podcast";
import { useSelector } from "react-redux";
import { PodcastEditState } from "../studio";
import { animated, useTransition } from "@react-spring/web";
import { FileService } from "src/utils/file.service";
import Tooltip from "src/components/tooltip";
import { toast } from "react-toastify";

import styles from "./configurations.module.scss";
import { DURATION_CHOICES, PODCAST_TYPES } from "src/constants/podcast";
import { hasInsufficientContentError } from "../../edit/helper";



interface PodcastConfigurationsProps {
  podcast: Podcast;
  value: PodcastEditState;
  // eslint-disable-next-line no-unused-vars
  onChange: (property: string, value: any) => void;
}

const PodcastConfigurations: React.FC<PodcastConfigurationsProps> = ({ podcast, value, onChange }) => {
  const dispatch = useDispatch();

  const [showAddAssets, setShowAddAssets] = useState(false);
  const { assets, duration, voicePreset, podcastType } = value;

  const handleAddAsset = () => {
    setShowAddAssets(true);
  };

  const handleDurationChange = (duration: any) => {
    onChange("duration", duration);
  };

  const handlePodcastTypeChange = (type: string) => {
    onChange("podcastType", type);
    onChange("voicePreset", null);
  };

  const handleAddAssetFinish = useCallback((assets: any) => {
    const invalidAssets = assets.filter((asset) => {
      const fileType = FileService.getFileType(asset.metadata);
      const allowedTypes = ["PDF", "PPT", "PPTX", "DOC", "DOCX", "GOOGLE_SLIDES", "LINK"];
      return !_.includes(allowedTypes, fileType);
    });

    if (_.size(invalidAssets) > 0) {
      toast.error("Only pptx, pdf assets can be added to podcasts");
      return false;
    }

    const filteredAssets = assets.filter((asset) => {
      const fileType = FileService.getFileType(asset.metadata);
      const allowedTypes = ["PDF", "PPT", "PPTX", "DOC", "DOCX", "GOOGLE_SLIDES", "LINK"];
      return _.includes(allowedTypes, fileType);
    });


    if (_.size(filteredAssets) === 0) {
      toast.error("No valid assets selected. Please select pptx, pdf assets.");
      return false;
    }
    
    const existingAssets = value.assets || [];
    let updatedAssets = [...existingAssets, ...filteredAssets];
    updatedAssets = _.uniqBy(updatedAssets, "id");

    if (_.size(updatedAssets) > 5) {
      toast.error("You can only add a maximum of 5 assets to a podcast.");
      return false;
    }

    onChange("assets", updatedAssets);
  }, [value.assets, onChange]);

  const handleRemoveAsset = (assetId: string) => {
    const assets = value.assets || [];
    const updated = assets.filter((asset) => asset.id !== assetId);

    onChange("assets", updated);
  };

  const handleVoicePresetChange = (preset: VoicePreset) => {
    onChange("voicePreset", preset);
  }

  const entityAction = (entity: any) => {
    if (entity.entityType === "asset") {
      const fileType = FileService.getFileType(entity.metadata);
      const allowedTypes = ["PDF", "PPT", "PPTX", "DOC", "DOCX", "GOOGLE_SLIDES", "LINK"];
      const disabled =
        fileType && !_.includes(allowedTypes, fileType);
      return (
        <Tooltip
          content="Only pptx, pdf assets can be added to podcasts"
          placement="auto"
          disabled={!disabled}
        >
          <button className={styles.action}>
            <i
              className={cx(
                styles.icon,
                disabled ? "paperflite-lock" : "paperflite-add",
              )}
            ></i>
          </button>
        </Tooltip>
      );
    }
    return false;
  };

  const assetSelectionFilter = (entity: any) => {
    if (entity.entityType === "asset") {
      const fileType = FileService.getFileType(entity.metadata);
      const allowedTypes = ["PDF", "PPT", "PPTX", "DOC", "DOCX", "GOOGLE_SLIDES", "LINK"];
      return _.includes(allowedTypes, fileType);
    }
    return false;
  }

  const handleCloseAddAssets = () => {
    setShowAddAssets(false);
  }

  console.log("podcastType", assets, podcastType);

  return (
    <div className={styles.container}>
      <div className={styles.left}>
        <div className={cx(styles.header, styles.stickyHeader)}>
          <h4>Generate a podcast with studio</h4>
        </div>
        <div className={styles.body}>
          <div className={cx(styles.field, styles.assets)}>
            <label className={styles.label}>source &amp; link</label>
            <p className={styles.description}>
              Upload/drag and drop any assets from Paperflite library
            </p>
            <div className={styles.content}>
              <div className={cx(styles.asset, styles.add)} onClick={handleAddAsset}>
                <div className={styles.icon}>
                  <i className="paperflite-add"></i>
                </div>
                <div className={styles.name}>Add assets</div>
              </div>
              {assets.map((asset) => {
                const hasError = hasInsufficientContentError(podcast, asset.id);

                return (
                  <div key={asset.id} className={styles.asset}>
                    <div className={styles.icon}>
                      <Thumbnail
                        className={styles.thumbnail}
                        width={120}
                        media={asset?.icon}
                        defaultImage="/images/assets/default_asset.jpg"
                      />
                      {hasError && (
                        <div className={styles.error}>
                          <Tooltip content="This asset has insufficient content" placement="top">
                            <i className={cx("paperflite-danger")}></i>
                          </Tooltip>
                        </div>
                      )}
                      <button className={styles.remove} onClick={() => handleRemoveAsset(asset.id)}>
                        <i className="paperflite-close"></i>
                      </button>
                    </div>
                    <div className={styles.name}>
                      <Text text={asset?.name} lines={1} width={56} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className={cx(styles.field, styles.duration)}>
            <label className={styles.label}>podcast duration</label>
            <p className={styles.description}>
              Select how much time you would like to spent learning
            </p>
            <div className={styles.content}>
              <DurationSlider
                value={duration}
                options={DURATION_CHOICES}
                onChange={handleDurationChange}
              />
            </div>
          </div>
          <div className={cx(styles.field, styles.podcastType)}>
            <label className={styles.label}>podcast type</label>
            <p className={styles.description}>
              Pick a podcast type that best suits your learning need
            </p>
            <div className={styles.content}>
              <PodcastTypeField
                value={podcastType}
                onChange={handlePodcastTypeChange}
              />
            </div>
          </div>

          {podcastType && (
            <VoicePresetsField podcastType={podcastType} value={voicePreset} onChange={handleVoicePresetChange} />
          )}

        </div>
      </div>
      <div className={styles.right}>
        <ChatContainer podcast={podcast} />
      </div>

      <AddAssetsDialog
        show={showAddAssets}
        selectableEntityTypes={["asset"]}
        source={{
          type: "course",
          entity: podcast as any,
        }}
        allowInternalEntities={true}
        allowBackgroundUpload={false}
        entityAction={entityAction}
        selectionFilter={assetSelectionFilter}
        onFinish={handleAddAssetFinish}
        onClose={handleCloseAddAssets}
      />
    </div>
  );
};

interface DurationSliderProps {
  options: {
    label: string;
    value: any;
  }[];
  value: any;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: any) => void;
}

export const DurationSlider: React.FC<DurationSliderProps> = ({ value, onChange, options }) => {
  const [slideRef, slideBounds] = useMeasure();
  const [ticks, setTicks] = React.useState(0);
  const [valuePosition, setValuePosition] = React.useState(0);
  const [progressStyle, setProgressStyle] = React.useState<any>({
    left: 0,
    width: 0,
  });


  const index = React.useMemo(() => {
    const index = _.findIndex(options, (option) => {
      return option.value === value;
    });
    return index === -1 ? options.length : index;
  }, [value, options]);

  const valueLabel = React.useMemo(() => {
    const index = _.findIndex(options, (option) => {
      return option.value === value;
    });
    return index === -1 ? "i'm flexible" : options[index].label;
  }, [value, index, options]);

  useEffect(() => {
    if (slideBounds.width > 0) {
      const width = slideBounds.width - 28;
      setTicks(Math.floor(width / 6));
    }
  }, [slideBounds]);

  useEffect(() => {
    if (slideBounds.width > 0) {
      const width = slideBounds.width - 34;
      const position = index * ((width - 34) / options.length);
      setValuePosition(position);
    }
  }, [slideBounds, index]);

  useEffect(() => {
    if (slideBounds.width > 0) {
      const width = slideBounds.width - 34;
      const progressWidth = Math.max((index * (width / options.length)) - 6, 0);
      const emptyDuration = index === options.length;

      setProgressStyle({
        left: emptyDuration ? 0 : 0,
        width: emptyDuration ? width - 8 : progressWidth,
      });
    }
  }, [slideBounds, index]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);

    let output = null;
    if (value >= 0 && value < options.length) {
      output = options[value].value;
    }

    onChange(output);
  }

  return (
    <div className={styles.slide}>
      <div className={styles.wrapper} ref={slideRef}>
        <div className={styles.track}>
          {_.times(ticks, (index) => (
            <div key={index} className={styles.tick} />
          ))}
          <div className={cx(styles.end, {
            [styles.active]: index === options.length,
          })} />
        </div>
        <div className={styles.value} style={{
          left: `${valuePosition}px`,
        }}>
          <span>{valueLabel}</span>
        </div>
        <div className={cx(styles.progress, {
          [styles.empty]: index === options.length,
        })} style={progressStyle} />
        <input type="range" className={styles.slider}
          min={0} max={_.size(options)}
          value={index}
          onChange={handleSliderChange}
        />
      </div>
    </div>
  );
}


interface VoicePresetPanelProps {
  voice: VoicePreset;
  playing: boolean;

  // eslint-disable-next-line no-unused-vars
  onPlay?: (voice: VoicePreset) => void;

  // eslint-disable-next-line no-unused-vars
  onStop?: (voice: VoicePreset) => void;

}

const VoicePresetPanel: React.FC<VoicePresetPanelProps> = ({ voice, playing, onPlay, onStop }) => {
  const audio = useMemo(() => {
    const url = voice.previewUrl;
    const audio = new Audio();
    audio.preload = "none";
    audio.src = url;

    return audio;
  }, [voice.id]);

  const { id, name } = voice;

  const [loaded, setLoaded] = useState(false);

  const playTransition = useTransition(!playing, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const stopTransition = useTransition(playing, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const handlePlay = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (onPlay) {
      onPlay(voice);
    }
  }

  const handleStop = (e) => {
    e?.stopPropagation();
    e?.preventDefault();

    if (onStop) {
      onStop(voice);
    }
  }

  useEffect(() => {
    audio.addEventListener('ended', () => handleStop(null));
    audio.addEventListener('pause', () => handleStop(null));
    return () => {
      audio.removeEventListener('ended', () => handleStop(null));
      audio.removeEventListener('pause', () => handleStop(null));
    };
  }, []);

  useEffect(() => {
    if (playing) {
      if (!loaded) {
        audio.load();
        audio.addEventListener('canplaythrough', function () {
          setLoaded(true);
        }, false);
      } else {
        audio.play();
      }
    } else {
      audio.pause();
      audio.currentTime = 0;
    }
  }, [playing, loaded]);

  return (
    <div className={styles.voicePreset}>
      <div className={styles.inner}>
        <div className={styles.icon}>
          <img src="/images/coaching/podcast_host.svg" alt="Host" />
        </div>
        <div className={styles.actions}>
          {playTransition((style, item) =>
            item ? (
              <animated.button style={style} className={cx(styles.action, styles.play)} onClick={handlePlay}>
                <i className="paperflite-play" />
              </animated.button>
            ) : null
          )}

          {stopTransition((style, item) =>
            item ? (
              <animated.button style={style} className={cx(styles.action, styles.stop)} onClick={handleStop}>
                <i className="paperflite-pause" />
              </animated.button>
            ) : null
          )}

        </div>
        <div className={styles.icon}>
          <img src="/images/coaching/podcast_host.svg" alt="Host" />
        </div>
      </div>
    </div>
  );
};

interface VoicePresetsFieldProps {
  podcastType: string;
  value?: VoicePreset | null;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: VoicePreset) => void;
}

const VoicePresetsField: React.FC<VoicePresetsFieldProps> = ({ podcastType, value, onChange }) => {
  const dispatch = useDispatch();
  const [previewVoice, setPreviewVoice] = useState<string | null>(null);

  const { loading, voicePresets } = useSelector((state: any) => {
    const loading = _.get(state, `coaching.podcasts.voicePresets['${podcastType}'].loading`);
    const items = _.get(state, `coaching.podcasts.voicePresets['${podcastType}'].items`) || [];

    return {
      loading: loading,
      voicePresets: items,
    };
  });

  const selectedIndex = useMemo(() => {
    return voicePresets.findIndex((preset) => preset.id === value?.id);
  }, [value, voicePresets]);

  useEffect(() => {
    if (_.size(voicePresets) == 0) {
      dispatch(fetchVoicePresets(podcastType));
    }
  }, [podcastType, voicePresets]);

  useEffect(() => {
    if(!value && _.size(voicePresets) > 0) {
      const defaultVoice = voicePresets.find((preset) => preset.systemDefault);
      onChange(defaultVoice);
    }
  }, [value, voicePresets]);

  const handleChange = (index: number) => {
    if (index < 0 || index >= voicePresets.length) {
      return;
    }
    const selectedPreset = voicePresets[index];
    onChange(selectedPreset);
    setPreviewVoice(null);
  };

  const handlePlay = (voice: VoicePreset) => {
    setPreviewVoice(voice.id);
  }

  const handleStop = () => {
    setPreviewVoice(null);
  }

  if (loading) {
    return <div></div>;
  }

  return (
    <div className={cx(styles.field, styles.voice)}>
      <label className={styles.label}>voice</label>
      <p className={styles.description}>
        Select the tone you&apos;d like your host to speak in
      </p>
      <div className={styles.content}>
        <Carousel
          activeIndex={selectedIndex}
          controls={false}
          indicators={false}
        >
          {voicePresets.map((option, index) => (
            <Carousel.Item key={index}>
              <VoicePresetPanel voice={option} playing={previewVoice === option.id} onPlay={handlePlay} onStop={handleStop} />
            </Carousel.Item>
          ))}
        </Carousel>
        <div className={cx(styles.controls)}>
          <button
            className={cx(styles.action, styles.left)}
            disabled={selectedIndex == 0}
            onClick={() => handleChange(selectedIndex - 1)}
          >
            <i className="paperflite-dropdown"></i>
          </button>
          <span className={styles.name}>{value?.name || null}</span>
          <button
            className={cx(styles.action, styles.right)}
            disabled={selectedIndex >= voicePresets.length - 1}
            onClick={() => handleChange(selectedIndex + 1)}
          >
            <i className="paperflite-dropdown"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

// Podcast type field
interface PodcastTypeFieldProps {
  value?: string | null;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: string) => void;
}

export const PodcastTypeField: React.FC<PodcastTypeFieldProps> = ({ value, onChange }) => {
  const selectedIndex = useMemo(() => {
    return PODCAST_TYPES.findIndex((type) => type.id === value);
  }, [value, PODCAST_TYPES]);

  const handleChange = (index: number) => {
    if (index < 0 || index >= PODCAST_TYPES.length) {
      return;
    }
    const selectedType = PODCAST_TYPES[index];
    onChange(selectedType.id);
  };

  return (
    <div className={styles.podcastTypeSelector}>
      <button
        className={cx(styles.action, styles.left)}
        disabled={selectedIndex == 0}
        onClick={() => handleChange(selectedIndex - 1)}
      >
        <i className="paperflite-dropdown"></i>
      </button>
      <Carousel
        activeIndex={selectedIndex}
        controls={false}
        indicators={false}
      >
        {PODCAST_TYPES.map((option, index) => (
          <Carousel.Item key={index}>
            <div className={styles.podcastTypeOption}>
              <i className={cx(styles.icon, option.icon)}></i>
              <span className={styles.optionName}>{option.value}</span>
            </div>
          </Carousel.Item>
        ))}
      </Carousel>
      <button
        className={cx(styles.action, styles.right)}
        disabled={selectedIndex >= PODCAST_TYPES.length - 1}
        onClick={() => handleChange(selectedIndex + 1)}
      >
        <i className="paperflite-dropdown"></i>
      </button>
    </div>
  );
}

export default PodcastConfigurations;
